export type ClientErrorType =
  | "react.error-boundary"
  | "window.error"
  | "unhandledrejection";

export type ClientErrorPayload = {
  type: ClientErrorType;
  message: string;
  stack: string | null;
  url: string | null;
  filename: string | null;
  line: number | null;
  column: number | null;
  timestamp: string;
  userAgent: string | null;
  componentStack: string | null;
};

type CreateClientErrorPayloadInput = {
  type: ClientErrorType;
  error?: unknown;
  message?: string | null;
  stack?: string | null;
  url?: string | null;
  filename?: string | null;
  line?: number | null;
  column?: number | null;
  timestamp?: string;
  userAgent?: string | null;
  componentStack?: string | null;
  preferBeacon?: boolean;
};

function toNullableString(value: unknown): string | null {
  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }

  return null;
}

function toNullableNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function normalizeUnknownError(error: unknown) {
  if (error instanceof Error) {
    return {
      message: error.message || "Unexpected client error",
      stack: toNullableString(error.stack),
    };
  }

  if (typeof error === "string") {
    return {
      message: error,
      stack: null,
    };
  }

  if (error && typeof error === "object") {
    const record = error as Record<string, unknown>;

    return {
      message:
        toNullableString(record.message) ||
        toNullableString(record.toString?.()) ||
        "Unexpected client error",
      stack: toNullableString(record.stack),
    };
  }

  return {
    message: "Unexpected client error",
    stack: null,
  };
}

function getCurrentUrl() {
  if (typeof window === "undefined") {
    return null;
  }

  return toNullableString(window.location?.href);
}

function getUserAgent() {
  if (typeof navigator === "undefined") {
    return null;
  }

  return toNullableString(navigator.userAgent);
}

function removeTrailingSlash(value: string) {
  return value.replace(/\/+$/, "");
}

export function getClientErrorEndpoint() {
  const baseUrl = toNullableString(process.env.NEXT_PUBLIC_BASE_URL);

  if (baseUrl) {
    return `${removeTrailingSlash(baseUrl)}/api/client-error`;
  }

  if (typeof window !== "undefined") {
    return `${removeTrailingSlash(window.location.origin)}/api/client-error`;
  }

  return "/api/client-error";
}

export function createClientErrorPayload(
  input: CreateClientErrorPayloadInput
): ClientErrorPayload {
  const normalizedError = normalizeUnknownError(input.error);

  return {
    type: input.type,
    message:
      toNullableString(input.message) ||
      normalizedError.message ||
      "Unexpected client error",
    stack: toNullableString(input.stack) || normalizedError.stack,
    url: toNullableString(input.url) || getCurrentUrl(),
    filename: toNullableString(input.filename),
    line: toNullableNumber(input.line),
    column: toNullableNumber(input.column),
    timestamp: input.timestamp || new Date().toISOString(),
    userAgent: toNullableString(input.userAgent) || getUserAgent(),
    componentStack: toNullableString(input.componentStack),
  };
}

function shouldUseBeacon(preferBeacon?: boolean) {
  if (preferBeacon) {
    return true;
  }

  if (typeof document === "undefined") {
    return false;
  }

  return document.visibilityState === "hidden";
}

export async function reportClientError(
  input: CreateClientErrorPayloadInput
): Promise<void> {
  const payload = createClientErrorPayload(input);
  const endpoint = getClientErrorEndpoint();

  try {
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.sendBeacon === "function" &&
      shouldUseBeacon(input.preferBeacon)
    ) {
      const blob = new Blob([JSON.stringify(payload)], {
        type: "application/json",
      });
      navigator.sendBeacon(endpoint, blob);
      return;
    }

    if (typeof fetch === "function") {
      await fetch(endpoint, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
    }
  } catch (loggingError) {
    if (typeof console !== "undefined" && typeof console.error === "function") {
      console.error("[client-error-logger]", payload, loggingError);
    }
  }
}
